import React, { useState, useEffect } from 'react';
import { useForm } from '../../../context/FormContext';
import FormField from '../../common/FormField/FormField';
import CountrySelect from '../../common/CountrySelect/CountrySelect';
import PhoneCountrySelect from '../../common/PhoneCountrySelect/PhoneCountrySelect';
import PhoneInput from '../../common/PhoneInput/PhoneInput';
import { validateSection, isSectionComplete } from '../../../utils/validation';
import './ContactInformation.css';

const ContactInformation = ({ data, errors, onSave, isLoading, isReadOnly }) => {
  const { actions } = useForm();
  const [formData, setFormData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;

    const newData = { ...formData, [field]: value };
    setFormData(newData);
    setHasChanges(true);

    // Update context immediately for real-time validation
    actions.updateSectionData('contactInformation', newData, 'in_progress');
  };

  const handleSave = () => {
    if (hasChanges) {
      // Validate the section
      const validationErrors = validateSection('contactInformation', formData);
      const isComplete = isSectionComplete('contactInformation', formData);

      // Set validation errors in context
      actions.setSectionErrors('contactInformation', validationErrors);

      // Determine status based on completion
      const status = isComplete ? 'completed' : 'in_progress';

      // Update section data with proper status
      actions.updateSectionData('contactInformation', formData, status);

      // Call the save handler
      onSave(formData);
      setHasChanges(false);
    }
  };

  // Auto-save when user stops typing
  // Removed auto-saving - users must manually save sections

  return (
    <div className="contact-information">
      <div className="section-intro">
        <p>Please provide the primary contact information for your company.</p>
      </div>

      <div className="form-grid">
        <FormField
          label="Contact person name"
          required
          error={errors.contactName}
          className="grid-span-2"
        >
          <input
            type="text"
            className={`form-input ${errors.contactName ? 'error' : ''}`}
            value={formData.contactName || ''}
            onChange={(e) => handleChange('contactName', e.target.value)}
            placeholder="Full name of primary contact"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Position in company"
          required
          error={errors.position}
          className="grid-span-2"
        >
          <input
            type="text"
            className={`form-input ${errors.position ? 'error' : ''}`}
            value={formData.position || ''}
            onChange={(e) => handleChange('position', e.target.value)}
            placeholder="e.g., CEO, Director, Manager"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Professional email"
          required
          error={errors.email}
          className="grid-span-2"
        >
          <input
            type="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            value={formData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="contact@company.com"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Phone number"
          required
          error={errors.phoneNumber || errors.phoneCountry}
          className="grid-span-2"
        >
          <div className="phone-input-group">
            <PhoneCountrySelect
              value={formData.phoneCountry || ''}
              onChange={(value) => handleChange('phoneCountry', value)}
              error={!!errors.phoneCountry}
              disabled={isReadOnly}
            />
            <PhoneInput
              value={formData.phoneNumber || ''}
              onChange={(value) => handleChange('phoneNumber', value)}
              error={!!errors.phoneNumber}
              placeholder="Phone number"
              disabled={isReadOnly}
            />
          </div>
        </FormField>
      </div>

      <div className="address-section">
        <h3>Contact Address</h3>

        <div className="form-grid">
          <FormField
            label="Address"
            required
            error={errors.address}
            className="grid-span-2"
          >
            <input
              type="text"
              className={`form-input ${errors.address ? 'error' : ''}`}
              value={formData.address || ''}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Street address"
              disabled={isReadOnly}
            />
          </FormField>

          <FormField
            label="City"
            required
            error={errors.city}
          >
            <input
              type="text"
              className={`form-input ${errors.city ? 'error' : ''}`}
              value={formData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City"
              disabled={isReadOnly}
            />
          </FormField>

          <FormField
            label="Postcode"
            required
            error={errors.postcode}
          >
            <input
              type="text"
              className={`form-input ${errors.postcode ? 'error' : ''}`}
              value={formData.postcode || ''}
              onChange={(e) => handleChange('postcode', e.target.value)}
              placeholder="Postcode"
              disabled={isReadOnly}
            />
          </FormField>

          <FormField
            label="Country"
            required
            error={errors.country}
            className="grid-span-2"
          >
            <CountrySelect
              value={formData.country || ''}
              onChange={(value) => handleChange('country', value)}
              error={!!errors.country}
              disabled={isReadOnly}
            />
          </FormField>
        </div>
      </div>

      {!isReadOnly && (
        <div className="section-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              'Save Section'
            )}
          </button>

          {hasChanges && (
            <span className="unsaved-indicator">
              You have unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactInformation;
